import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { saveTokens } from '../services/authService';
import { Loader2 } from 'lucide-react';

/*
 * Handles the Emergent Google Auth callback.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 *
 * Emergent redirects back to our app with `#session_id=...` in the URL fragment.
 * We POST that session_id to our backend, which exchanges it for app JWT tokens,
 * saves them to localStorage, then reloads `/dashboard` so AuthProvider picks them up.
 */
export default function AuthCallback() {
    const hasProcessed = useRef(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (hasProcessed.current) return;
        hasProcessed.current = true;

        const hash = window.location.hash || '';
        const match = hash.match(/session_id=([^&]+)/);
        if (!match) {
            setError('Missing session_id in callback URL');
            return;
        }

        const sessionId = decodeURIComponent(match[1]);

        axios
            .post('/api/auth/google/exchange', { session_id: sessionId })
            .then((res) => {
                const { access_token, refresh_token } = res.data;
                saveTokens(access_token, refresh_token);
                // Clean fragment, replace history entry, then hard-navigate so
                // AuthProvider re-checks auth from scratch with the new tokens.
                window.location.replace('/dashboard');
            })
            .catch((err) => {
                const msg = err.response?.data?.detail || err.message || 'Google sign-in failed';
                setError(typeof msg === 'string' ? msg : 'Google sign-in failed');
            });
    }, []);

    if (error) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center p-6" data-testid="auth-callback-error">
                <div className="max-w-md text-center">
                    <div className="inline-block px-3 py-1 rounded-full bg-rose-50 text-rose-600 text-xs font-bold uppercase tracking-wider mb-4">
                        Sign-in failed
                    </div>
                    <h1 className="text-2xl font-black text-zinc-950 mb-2">Something went wrong</h1>
                    <p className="text-sm text-zinc-600 mb-6">{error}</p>
                    <a
                        href="/login"
                        className="inline-block px-6 py-3 rounded-full bg-[#7C3AED] text-white font-bold text-sm hover:bg-[#6D28D9] transition-colors"
                    >
                        Back to sign in
                    </a>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white flex items-center justify-center p-6" data-testid="auth-callback-loading">
            <div className="text-center">
                <Loader2 className="w-8 h-8 text-[#7C3AED] animate-spin mx-auto mb-3" />
                <p className="text-sm font-semibold text-zinc-700">Signing you in…</p>
            </div>
        </div>
    );
}
